import { useMutation, useQuery } from "@tanstack/react-query";
import { ApiServiceErr, axiosApi } from "./apiService";

export interface MappingVersion {
  _id: string;
  drawingToolId: string;
  versionNumber: number;
  versionName: string;
  reportingYear?: number | null;
  reason: string;
  isDefault: boolean;
  isCalculated: boolean;
  lastEditedBy?: string;
  lastEditedAt?: Date;
  drawingName: string;
  drawingNote: string;
  chartNodes: any[];
  chartEdges: any[];
  lockStatus: boolean;
  snapshotData?: {
    ingredients?: any[];
    farmGate?: any[];
    processGate?: any[];
    externalStorage?: any[];
    externalTransportation?: any[];
    mappingIngredients?: any[];
  };
  createdAt: Date;
  updatedAt: Date;
}

export interface CreateVersionDto {
  drawingToolId: string;
  reason: string;
}

export interface CalculationRun {
  _id: string;
  mappingVersionId: string;
  productSummaryId: string;
  billOfMaterialSummaryId: string;
  climateImpact: number;
  impactUnit: string;
  reason: string;
  createdAt: Date;
  createdBy: string;
}

// ProductSummary interface for calculation history
export interface ProductSummaryCalculation {
  _id: string;
  productId: string;
  productName: string;
  salesUnitId: string;
  salesUnitName: string;
  drawingId: string;
  mappingVersionId: string;
  totalImpact: {
    climateChange: {
      measure: number;
      unit: string;
    };
  };
  createdAt: Date;
  createdBy: string;
}

export interface RestoreResult {
  restoredData: {
    ingredients: Record<string, string>;
    farmGates: Record<string, string>;
    processGates: Record<string, string>;
    externalStorages: Record<string, string>;
    externalTransportations: Record<string, string>;
  };
  updatedChartNodes: any[];
  updatedChartEdges: any[];
}

/**
 * Create a new version of a mapping
 * This is called when user clicks "Edit" after a mapping has been calculated
 */
export const useCreateNewVersion = () => {
  return useMutation<MappingVersion, ApiServiceErr, CreateVersionDto>(async data => {
    const response = await axiosApi.post("/mapping-versions/create", data);
    return response.data;
  });
};

/**
 * Restore a version's snapshot data back to original collections
 */
export const useRestoreVersionData = () => {
  return useMutation<
    { message: string; result: RestoreResult },
    ApiServiceErr,
    { versionId: string }
  >(async ({ versionId }) => {
    const response = await axiosApi.post(`/mapping-versions/restore/${versionId}`);
    return response.data;
  });
};

export interface DeleteVersionResult {
  deletedVersionId: string;
  deletedVersionNumber: number;
  /** True when the removed version was the active one, so a successor was activated in its place. */
  wasActive: boolean;
  /**
   * The version the mapping is left on. Null when the deleted version was the mapping's only one
   * -- in that case the mapping itself was removed (chart, versions and all), not merely emptied.
   */
  activeVersion: MappingVersionSummary | null;
  /** The chart to re-render. Null when the active version did not change. */
  updatedChartNodes: any[] | null;
  updatedChartEdges: any[] | null;
  remainingVersionCount: number;
}

export interface MappingVersionSummary {
  _id: string;
  versionNumber: number;
  versionName: string;
  isDefault: boolean;
  isCalculated: boolean;
  reportingYear: number | null;
}

/**
 * Remove a single mapping version. When the removed version is the active one the backend
 * activates the newest remaining version in its place, so the mapping is never left without one.
 * When it is the mapping's only version, the mapping itself is removed instead -- chart nodes,
 * every version record and the drawing-tool document -- rather than being left empty
 * (`activeVersion` comes back `null`, `remainingVersionCount` is `0`; treat the response as a
 * full mapping removal and navigate/refetch accordingly, not as "mapping still exists, just
 * empty"). ProductSummary history is preserved either way.
 */
export const useDeleteMappingVersion = () => {
  return useMutation<
    { message: string; result: DeleteVersionResult },
    ApiServiceErr,
    { versionId: string }
  >(async ({ versionId }) => {
    const response = await axiosApi.delete(`/mapping-versions/${versionId}`);
    return response.data;
  });
};

/**
 * Get version history for a drawing tool
 */
export const useGetVersionHistory = (drawingToolId: string, enabled: boolean = true) => {
  return useQuery<MappingVersion[], ApiServiceErr>(
    ["version-history", drawingToolId],
    async () => {
      const response = await axiosApi.get("/mapping-versions/history", {
        params: { drawingToolId }
      });
      return response.data;
    },
    {
      enabled: enabled && !!drawingToolId
    }
  );
};

/**
 * Get a specific version by ID
 */
export const useGetVersionById = (versionId: string, enabled: boolean = true) => {
  return useQuery<MappingVersion, ApiServiceErr>(
    ["version", versionId],
    async () => {
      const response = await axiosApi.get(`/mapping-versions/${versionId}`);
      return response.data;
    },
    {
      enabled: enabled && !!versionId
    }
  );
};

/**
 * Get the default version for a drawing tool
 */
export const useGetDefaultVersion = (drawingToolId: string, enabled: boolean = true) => {
  return useQuery<MappingVersion, ApiServiceErr>(
    ["default-version", drawingToolId],
    async () => {
      const response = await axiosApi.get(`/mapping-versions/default/${drawingToolId}`);
      return response.data;
    },
    {
      enabled: enabled && !!drawingToolId
    }
  );
};

/**
 * Get the latest calculation for a version (ProductSummary)
 */
export const useGetLatestCalculation = (versionId: string, enabled: boolean = true) => {
  return useQuery<ProductSummaryCalculation, ApiServiceErr>(
    ["latest-calculation", versionId],
    async () => {
      const response = await axiosApi.get(`/mapping-versions/calculations/latest/${versionId}`);
      return response.data;
    },
    {
      enabled: enabled && !!versionId
    }
  );
};

